import { ActionMenuList, Button } from '@ds';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CodeMirror from '@uiw/react-codemirror';
import { getChartFormByType } from '@/reducers/dashboardChartsReducer';
import { usePythonPanelCode } from '@experiment-management-shared/api';
import classes from './PythonCodeTab.module.scss';
import { DSPlayIcon } from '@design-system/icons';
import dashboardChartsActions from '@/actions/dashboardChartsActions';
import { trackEvent } from '@shared/utils/eventTrack';
import { panelEvents } from '@/constants/trackingEventTypes';
import { generatePythonPanelCodeVersion } from '@experiment-management-shared/utils';
import { PythonPanelOptions } from '../PythonPanelOptions';
import { BUILT_IN_CHART_TYPES } from '@/experiment-management-shared/constants';
import { useHistory, useLocation } from 'react-router';
import { getURLQueryParams } from '@shared/utils/url';
import usePanelTemplate from '@/experiment-management-shared/api/library-panel/usePanelTemplate';
import cx from 'classnames';
import { LibraryPanelOptions } from '@/experiment-management-shared/components/AddBuiltInVisualizationModal/subcomponents/LibaryPanelTabs/LibraryPanelOptions';
import { getDefaultPythonCodeExtensions } from '@shared';
import { useRestartComputeEngine } from '@/experiment-management-shared/api/custom-server-panels/useRestartComputeEngine';
import isEmpty from 'lodash/isEmpty';
import {
  CodePanelTemplateCodeJs,
  CodePanelTemplateCodePy
} from '@experiment-management-shared/api/library-panel/types';

const DEBOUNCE_DELAY = 200;

const PythonPanelCodeTab = () => {
  const [code, setCode] = useState<string | null>(null);
  const location = useLocation();
  const queryParams = getURLQueryParams(location.hash);

  const chartType = queryParams.get('chartType') || '';
  const templateId = queryParams.get('templateId') || '';
  const revisionId = queryParams.get('revisionId') || '';

  const useRestartComputeEngineHook = useRestartComputeEngine();

  const dispatch = useDispatch();

  const panelForm = useSelector(state =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    /* @ts-ignore */
    getChartFormByType(state, { chartType })
  );

  const changePanelFormCode = useCallback(
    (c: string) => {
      dispatch(dashboardChartsActions.updateChartFormKey(chartType, 'code', c));
    },
    [dispatch, chartType]
  );

  const handleCodeChange = (newCode: string) => {
    changePanelFormCode(newCode);
  };

  const handleRestartSession = () => {
    useRestartComputeEngineHook.mutate();
  };

  const changePanelFormCodeVersion = useCallback(() => {
    dispatch(
      dashboardChartsActions.updateChartFormKey(
        chartType,
        'codeVersion',
        generatePythonPanelCodeVersion()
      )
    );
  }, [chartType, dispatch]);

  const onClickRun = () => {
    if (code !== null) {
      changePanelFormCodeVersion();
      trackEvent(panelEvents.CLICK_PYTHON_RUN);
    }
  };

  const debouncedClickRun = useCallback(debounce(onClickRun, DEBOUNCE_DELAY), [
    code
  ]);

  const {
    data: revisionCode = null,
    isFetched,
    isLoading: isLoadingRevision
  } = usePythonPanelCode(
    {
      revisionId: panelForm.revisionId,
      templateId: panelForm.templateId
    },
    {
      enabled: chartType === BUILT_IN_CHART_TYPES.python,
      refetchOnMount: true
    }
  );

  const {
    data: template,
    isFetched: isTemplateFetched,
    isLoading: isLoadingTemplate
  } = usePanelTemplate(
    {
      templateId,
      revisionId
    },
    {
      enabled: chartType == BUILT_IN_CHART_TYPES.libraryPanel && !!templateId,
      keepPreviousData: true,
      onSuccess: res => {
        if (res?.code.type === 'py') {
          changePanelFormCode(res?.code.pyCode);
        } else if (res?.code.type === 'js') {
          changePanelFormCode(res?.code.code);
        }
      }
    }
  );

  useEffect(() => {
    if (revisionCode !== null) {
      changePanelFormCode(revisionCode);
    }
  }, [changePanelFormCode, revisionCode]);

  useEffect(() => {
    if (template && isEmpty(panelForm.code)) {
      const code = template.code;
      changePanelFormCode(
        code.type === 'py'
          ? (code as CodePanelTemplateCodePy).pyCode
          : (code as CodePanelTemplateCodeJs).code
      );
    }
  }, [template]);

  useEffect(() => {
    if (template !== null && isTemplateFetched) {
      changePanelFormCodeVersion();
    }
  }, [changePanelFormCodeVersion, template, isTemplateFetched]);

  useEffect(() => {
    if (revisionCode !== null && isFetched) {
      changePanelFormCodeVersion();
    }
  }, [changePanelFormCodeVersion, revisionCode, isFetched]);

  useEffect(() => {
    if (!isLoadingTemplate && !isLoadingRevision) {
      setCode(panelForm.code);
    }
  }, [isLoadingRevision, isLoadingTemplate, panelForm.code]);

  const history = useHistory();

  const handleEdit = () => {
    const [hashPath, queryString] = history.location.hash.split('?');
    const searchParams = new URLSearchParams(queryString);

    searchParams.delete('isPreview');

    history.replace({
      ...location,
      hash: `${hashPath}?${searchParams.toString()}`
    });
  };

  const isPreview = queryParams.get('isPreview') === 'true';

  return (
    <>
      <div className={classes.pythonPanelActionsContainer}>
        {isPreview ? (
          <Button
            size="small"
            onClick={handleEdit}
            disabled={!code || template?.code?.type === 'js'}
          >
            Edit
          </Button>
        ) : (
          <Button
            size="small"
            PrefixIcon={<DSPlayIcon />}
            onClick={debouncedClickRun}
            loading={isLoadingRevision || isLoadingTemplate}
            disabled={!code || template?.code?.type === 'js'}
          >
            Run code
          </Button>
        )}
        <ActionMenuList data-test="python-panel-actions-btn">
          <ActionMenuList.Trigger className={classes.pythonPanelOptions} />
          {chartType === BUILT_IN_CHART_TYPES.python && (
            <PythonPanelOptions
              templateId={panelForm.templateId}
              revisionId={panelForm.revisionId}
              onRestartSession={handleRestartSession}
            />
          )}
          {chartType === BUILT_IN_CHART_TYPES.libraryPanel && (
            <LibraryPanelOptions
              templateId={templateId}
              selectedRevisionId={revisionId}
              onRestartSession={handleRestartSession}
              template={template}
            />
          )}
        </ActionMenuList>
      </div>
      <div
        className={cx(classes.tab, {
          // Legacy Custom Charts allows to have js code
          [classes.disabled]: isPreview || template?.code?.type === 'js'
        })}
      >
        <CodeMirror
          data-test="python-code-mirror"
          value={code || ''}
          editable={!isPreview && template?.code?.type !== 'js'}
          extensions={getDefaultPythonCodeExtensions()}
          onChange={handleCodeChange}
          className={classes.pythonCodeMirror}
        />
      </div>
    </>
  );
};

export default PythonPanelCodeTab;
